import {IStateStore}         from '../../ap-interface';
import {Injectable}          from '@angular/core';
import {Store}               from '../index';
import {ApSignalrService}    from '../../ap-core/services/ap-signalr.service';
import {
  ApCustomTypes,
  MonitoringDetailAttachmentDownload,
  MonitoringDetailAttachmentDownloadFail,
  MonitoringDetailAttachmentDownloadSuccess,
  MonitoringDetailAttachmentUpload,
  MonitoringDetailAttachmentUploadFail,
  MonitoringDetailAttachmentUploadSuccess,
  MonitoringDetailCreate,
  MonitoringDetailDelete,
  MonitoringDetailEdit,
  MonitoringDetailLoad,
  MonitoringDetailLoadFail,
  MonitoringDetailLoadSuccess,
  NetTypes
}                            from 'invoker-transport';
import {SafeBehaviorSubject} from 'ts-tooling';
import IGuid = System.IGuid;
import IMonitoringDetail = Data.Fields.IMonitoringDetail;
import IMonitoringDetailAttachment = Data.Fields.IMonitoringDetailAttachment;
import {debounceTime}        from 'rxjs/operators';
import {APP_CONFIGURATION}   from '../../ap-core/config';

interface IMonitoringDetailStore extends IStateStore<IMonitoringDetail> {
}

@Injectable({providedIn: 'root'})
export class MonitoringDetailStore extends Store<IMonitoringDetailStore> {
  constructor(public backend: ApSignalrService) {
    super(backend, {
      loaded: false,
      loading: false,
      data: []
    });
    backend.registerObservable(MonitoringDetailLoadSuccess).subscribe(d => {
      super.Mutate(s => s.data, () => d.Data);
      super.SetLoadFinishState();
    });
    backend.registerObservable(MonitoringDetailLoadFail).subscribe(_ => {
      super.SetLoadFailState();
    });
    this.AfterDatabaseUpdate.pipe(debounceTime(APP_CONFIGURATION.StoreMutateDebounce)).subscribe(() => this.ReloadSource());
  }

  /**
   * Gets the loading state observable
   * @returns Observable indicating whether data is being loaded
   * @public
   */
  public get Loading$(): SafeBehaviorSubject<boolean> {
    return super.Listen(s => s.loading);
  }

  /**
   * Gets the monitoring details data stream
   * @returns Observable of monitoring details array
   * @public
   */
  public get MonitoringDetails$(): SafeBehaviorSubject<IMonitoringDetail[]> {
    return this.Listen((s) => s.data);
  }

  /**
   * Gets the current value of monitoring details
   * @returns Loaded monitoring details
   * @public
   */
  public get MonitoringDetails(): IMonitoringDetail[] {
    return this.MonitoringDetails$.getValue();
  }

  /**
   * Initiates loading of monitoring details from backend
   * @public
   */
  public loadMonitoringDetails(): void {
    super.SetLoadState();
    this.DispatchBackend(new MonitoringDetailLoad([]));
  }

  /**
   * Creates a new monitoring detail record
   * @param monitoringDetail - Detail information to create
   * @public
   */
  public createMonitoringDetail(monitoringDetail: IMonitoringDetail): void {
    this.DispatchBackend(new MonitoringDetailCreate([
      {Name: 'monitoringDetail', Type: ApCustomTypes.Data_Fields_MonitoringDetail, Value: monitoringDetail}
    ]));
  }

  /**
   * Updates an existing monitoring detail record
   * @param monitoringDetail - Updated detail information
   * @public
   */
  public editMonitoringDetail(monitoringDetail: IMonitoringDetail): void {
    this.DispatchBackend(new MonitoringDetailEdit([
      {Name: 'monitoringDetail', Type: ApCustomTypes.Data_Fields_MonitoringDetail, Value: monitoringDetail}
    ]));
  }

  /**
   * Deletes a monitoring detail by its Id
   * @param monitoringDetailId - Unique identifier of the detail to delete
   * @public
   */
  public deleteMonitoringDetail(monitoringDetailId: IGuid): void {
    this.DispatchBackend(new MonitoringDetailDelete([
      {Name: 'monitoringDetailId', Type: NetTypes.GUID, Value: monitoringDetailId}
    ]));
  }

  /**
   * Uploads an attachment for a monitoring detail
   * @param attachment - Attachment information to upload
   * @returns Promise that resolves when upload is successful
   * @public
   */
  public uploadAttachment(attachment: IMonitoringDetailAttachment): Promise<void> {
    return new Promise((resolve, reject) => {
      this.DispatchBackend(new MonitoringDetailAttachmentUpload([
        {Name: 'attachment', Type: ApCustomTypes.Data_Fields_MonitoringDetailAttachment, Value: attachment}
      ]))
        .watchStream({
          action: MonitoringDetailAttachmentUploadSuccess,
          todo: () => resolve()
        })
        .watchStream({
          action: MonitoringDetailAttachmentUploadFail,
          todo: _ => reject()
        });
    });
  }

  /**
   * Downloads an attachment by its Id
   * @param attachmentId - Unique identifier of the attachment to download
   * @returns Promise that resolves with the attachment data
   * @public
   */
  public downloadAttachment(attachmentId: IGuid): Promise<IMonitoringDetailAttachment> {
    return new Promise((resolve, reject) => {
      this.DispatchBackend(new MonitoringDetailAttachmentDownload([
        {Name: 'attachmentId', Type: NetTypes.GUID, Value: attachmentId}
      ]))
        .watchStream({
          action: MonitoringDetailAttachmentDownloadSuccess,
          todo: (result) => resolve(result.Data)
        })
        .watchStream({
          action: MonitoringDetailAttachmentDownloadFail,
          todo: _ => reject()
        });
    });
  }
}
